import pluralizer from 'pluralize';
import { daysFromNow, formatTimeSpan } from '@zapier/date';
import { TaskHeldReason } from '@zapier/design-system-reporting/src/graphql-types';

import { PRICING_PAGE_URL } from '@zapier/design-system-revenue';
import {
  AUTHS_URL,
  CREDIT_CARD_URL,
  HELD_FOLDER_URL,
  HELD_STATUSES_URL,
  REPLAY_STEP,
} from '../constants';

import { Step } from '../types';
import {
  GetTasksHeldBanner_account as Account,
  GetTasksHeldBanner_account_heldTasksByReason as Aggregation,
  GetTasksHeldBanner_account_heldTasksByReason_reasons_auths as Auth,
} from './types/GetTasksHeldBanner';

/**
 * Turns the held task aggregation into a list of steps for the user
 * to take to resolve the issues
 */
export function getSteps(
  account: Account,
  previous: Aggregation | undefined
): Step[] {
  const aggregation = account?.heldTasksByReason;
  const roots = aggregation?.reasons || [];
  const total = aggregation?.totalHeldTaskCount || 0;

  const hasThrottled = roots.some(
    root => root.reason === TaskHeldReason.throttled
  );
  const hasTooMany = roots.some(
    root => root.reason === TaskHeldReason.too_many
  );
  const hasPlanLimit = roots.some(
    root => root.reason === TaskHeldReason.plan_limit
  );

  const rawSteps = roots
    // @ts-ignore we can reduce over roots
    .reduce((acc, root) => {
      // Find the matching previous root
      const previousRoot = previous?.reasons?.find(
        r => r.reason === root.reason
      );
      const isDone = isStepDone(root.total, previousRoot?.total || 0);

      switch (root.reason) {
        case TaskHeldReason.paymentIssue:
          return [
            ...acc,
            {
              cta: 'Update your payment source.',
              ctaUrl: CREDIT_CARD_URL,
              description: `We’re holding your ${pluralizer(
                'Task',
                root.total
              )} because of a payment issue.`,
              isActionable: true,
              isDone: !account.lastChargeFail,
              key: root.reason,
              sortOrder: 1,
            },
          ];

        case TaskHeldReason.plan_limit:
          return [
            ...acc,
            {
              cta: 'Upgrade your Zapier plan.',
              ctaUrl: PRICING_PAGE_URL,
              description: `You've exceeded your Zapier plan limits.`,
              isActionable: true,
              isDone:
                account?.plan?.tasks === -1 ||
                account?.plan?.tasks > root.total,
              key: root.reason,
              sortOrder: 2,
            },
          ];

        case TaskHeldReason.blacklisted_app:
          // Only show plan limit if held for that reason too
          if (hasPlanLimit) return acc;

          return [
            ...acc,
            {
              cta: 'Upgrade your Zapier plan.',
              ctaUrl: PRICING_PAGE_URL,
              description: 'You are using a Preimium App',
              isActionable: true,
              isDone: account?.plan?.hasPremiumApps,
              key: root.reason,
              sortOrder: 2,
            },
          ];

        case TaskHeldReason.stale_auth:
          return [
            ...acc,
            ...(previousRoot?.auths || root.auths || []).map(auth => {
              return {
                cta: `Reconnect ${auth.title}`,
                ctaUrl: AUTHS_URL,
                description: 'to your Zapier account.',
                isActionable: true,
                isDone:
                  isDone &&
                  findAuthById(previousRoot?.auths, auth.id) &&
                  !findAuthById(root.auths, auth.id),
                key: `${root.reason}-${auth.id}`,
                sortOrder: 3,
              };
            }),
          ];

        case TaskHeldReason.throttled:
        case TaskHeldReason.too_many:
          return [
            ...acc,
            {
              cta: 'Confirm that you want to play these Tasks.',
              ctaUrl: HELD_STATUSES_URL,
              description:
                'We held them because more Tasks than usual came through at once.',
              // This is not actionable if we have both types so we don't tell the user to replay
              // their tasks twice
              isActionable: !(hasThrottled && hasTooMany),
              isDone,
              key: root.reason,
              sortOrder: 4,
            },
          ];

        case TaskHeldReason.access_blacklisted_app:
          return [
            ...acc,
            {
              cta: 'Restricted App.',
              ctaUrl: HELD_FOLDER_URL,
              description:
                'You are using an app that has been restricted by your administrator',
              isActionable: true,
              isDone,
              key: root.reason,
              sortOrder: 5,
            },
          ];
      }

      return acc;
    }, [])
    // @ts-ignore we can sort steps
    .sort(compareBySortOrderOrTitle);

  return [
    ...rawSteps,
    {
      cta: `Replay ${total === 1 ? 'this' : 'these'} ${pluralizer(
        'Task',
        total
      )}.`,
      ctaUrl: HELD_STATUSES_URL,
      description: 'You can choose to play them individually or in bulk.',
      isActionable:
        (hasThrottled && hasTooMany) || (!hasThrottled && !hasTooMany),
      isDone: false,
      key: REPLAY_STEP,
      sortOrder: 90,
    },
  ];
}

function findAuthById(auths: Auth[] | null | undefined, id: string) {
  return (auths || []).find(auth => auth.id === id);
}

function isStepDone(count: number, prevCount: number) {
  return Boolean(prevCount && !count);
}

function compareBySortOrderOrTitle(f1: Step, f2: Step) {
  if (f1.sortOrder && f2.sortOrder) {
    return f1.sortOrder - f2.sortOrder;
  }

  if (f1.sortOrder || f2.sortOrder) {
    return f1.sortOrder ? -1 : 1;
  }

  return f1.cta.localeCompare(f2.cta);
}

export function getHeldTaskBannerTitle(
  numOfStepsDone: number,
  numOfStepsLeft: number,
  totalHeldTaskCount: number = 0
) {
  const tasks = pluralizer('Task', totalHeldTaskCount);
  // 'Cuz currently we can only see 10,000 tasks in TH page, and we want the banner count to match TH count.
  const visibleTaskLimit = 10000;
  const visibleTaskLimitString = `${visibleTaskLimit.toLocaleString()}+`;
  const totalNumOfHeldTasks =
    totalHeldTaskCount > visibleTaskLimit
      ? visibleTaskLimitString
      : totalHeldTaskCount.toLocaleString();

  // numOfStepsDone is the key one to pay attention to since it will be updated as user completes each step (tracked by FE only)
  if (numOfStepsDone > 0 && numOfStepsLeft > 1) {
    return `Great job! ${numOfStepsLeft} more fixes to play your ${totalNumOfHeldTasks} held ${tasks}!`;
  }

  // Only 1 step left (replay tasks step)
  if (numOfStepsDone > 0 && numOfStepsLeft === 1) {
    return `Great job! You can now play your held ${tasks}!`;
  }

  return `Don't lose your data from ${totalNumOfHeldTasks} held ${tasks}!`;
}

export function getDaysRemainingMessage(
  startsExpiringOn: string | null | undefined
) {
  if (!startsExpiringOn) {
    return {
      daysRemainText: '',
      msg: '',
    };
  }

  const totalDaysBetween = daysFromNow(startsExpiringOn);

  const daysRemainText = formatTimeSpan(Date.now(), startsExpiringOn, false);

  const msg = `${
    totalDaysBetween > 1 ? 'remain' : 'remains'
  } until we discard them.`;

  return {
    daysRemainText,
    msg,
  };
}
